import React from 'react';
import { Link, Typography } from '@mui/material';

const MARKDOWN_LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/gi;
const URL_RE = /https?:\/\/[^\s<>"'\\)]+/gi;
const TRAILING_PUNCT_RE = /[.,;:!?]+$/;

function isSafeHttpUrl(href) {
  try {
    const url = new URL(href);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (error) {
    return false;
  }
}

function trimTrailingPunctuation(url) {
  return url.replace(TRAILING_PUNCT_RE, '');
}

export function parseMessageLinks(text) {
  const source = String(text || '');
  const matches = [];

  MARKDOWN_LINK_RE.lastIndex = 0;
  let markdownMatch = MARKDOWN_LINK_RE.exec(source);
  while (markdownMatch) {
    if (isSafeHttpUrl(markdownMatch[2])) {
      matches.push({
        start: markdownMatch.index,
        end: markdownMatch.index + markdownMatch[0].length,
        href: markdownMatch[2],
        label: markdownMatch[1],
      });
    }
    markdownMatch = MARKDOWN_LINK_RE.exec(source);
  }

  URL_RE.lastIndex = 0;
  let urlMatch = URL_RE.exec(source);
  while (urlMatch) {
    const raw = trimTrailingPunctuation(urlMatch[0]);
    const start = urlMatch.index;
    const end = start + raw.length;
    const overlaps = matches.some((item) => start < item.end && end > item.start);
    if (!overlaps && isSafeHttpUrl(raw)) {
      matches.push({ start, end, href: raw, label: raw });
    }
    urlMatch = URL_RE.exec(source);
  }

  matches.sort((a, b) => a.start - b.start);

  const segments = [];
  let cursor = 0;
  matches.forEach((item) => {
    if (item.start > cursor) {
      segments.push({ type: 'text', value: source.slice(cursor, item.start) });
    }
    segments.push({ type: 'link', href: item.href, label: item.label });
    cursor = item.end;
  });
  if (cursor < source.length) {
    segments.push({ type: 'text', value: source.slice(cursor) });
  }
  return segments;
}

export default function LinkifiedText({ text, sx, ...typographyProps }) {
  const content = String(text || '').trim();
  if (!content) return null;

  const segments = parseMessageLinks(content);

  return (
    <Typography
      {...typographyProps}
      sx={{
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        ...sx,
      }}
    >
      {segments.map((segment, index) => {
        if (segment.type === 'link') {
          return (
            <Link
              key={`${segment.href}-${index}`}
              href={segment.href}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              {segment.label}
            </Link>
          );
        }
        return <React.Fragment key={`text-${index}`}>{segment.value}</React.Fragment>;
      })}
    </Typography>
  );
}
